import React from "react";

import { DeviceContext, NotificationContext, type NotificationPermissionState } from "~/lib/notifications";
import { Button } from "~/ui";

export function useDeviceNotificationsToggle(enabled: boolean): [showToggle: boolean, enableButton: React.ReactNode] {
	const [permission, setPermission] = React.useState<NotificationPermissionState | null>(null);
	const ctx = React.useContext(NotificationContext);

	const updatePermission = React.useCallback(() => {
		return ctx?.notificationPermission().then(p => setPermission(p));
	}, [ctx]);

	React.useEffect(() => {
		updatePermission();

		// Rerender if the user enables notifications in the iOS app settings
		window.addEventListener("focus", updatePermission);
		return () => window.removeEventListener("focus", updatePermission);
	}, [updatePermission]);

	const request = React.useCallback(async () => {
		if (ctx) {
			ctx.requestPermission({ skipToast: true });
			await ctx.register();
			await updatePermission();
		}
	}, [ctx, updatePermission]);

	if (permission === "granted" || permission === null || !enabled || !ctx) return [false, null];

	let enableButton: React.ReactNode;

	switch (ctx.platform) {
		case DeviceContext.IOS:
			enableButton = <a href="app-settings:">go to app settings</a>;
			break;
		case DeviceContext.DESKTOP:
			return [false, null];
		case DeviceContext.WEB:
			if (permission === "default") {
				enableButton = (
					<Button
						size="normal"
						style={{ width: "unset" }}
						onClick={request}
						title="Clicking this button will prompt you for permission"
					>
						Enable
					</Button>
				);
			} else {
				// permission === "denied"
				enableButton = "Click the lock icon in the address bar to enable notifications";
			}
			break;
		case DeviceContext.ANDROID:
			// Android notification permission doesn't need to be requested
			enableButton = null;
			break;
	}

	return [true, enableButton];
}
